import React from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import CloudOffIcon from '@mui/icons-material/CloudOff';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BoltIcon from '@mui/icons-material/Bolt';
import QueueIcon from '@mui/icons-material/Queue';
import StorageIcon from '@mui/icons-material/Storage';
import { colors, status } from '../../theme';

function MetricPill({ icon: Icon, label, value, tone }) {
  const palette = status[tone] || status.neutral;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 1.25,
        py: 0.85,
        borderRadius: 2,
        bgcolor: '#fff',
        border: `1px solid ${colors.gray[200]}`,
        minWidth: 0,
        flex: '1 1 160px',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: palette.bg,
          color: palette.text,
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, lineHeight: 1.1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: colors.gray[500],
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 700,
            fontSize: 13.5,
            color: colors.gray[800],
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

MetricPill.propTypes = {
  icon: PropTypes.elementType.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  tone: PropTypes.oneOf(['positive', 'negative', 'warning', 'neutral', 'info', 'purple', 'cyan']).isRequired,
};

function AutomationStatusCards({ summary, t }) {
  const generatedAt = summary.generated_at
    ? new Date(summary.generated_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : '-';
  const runtimeBusy = summary.active_jobs > 0 || summary.queued_jobs > 0;

  return (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      sx={{ width: '100%' }}
    >
      <MetricPill
        icon={summary.worker_online ? CloudDoneIcon : CloudOffIcon}
        label={t('automation.status.worker', 'Worker')}
        value={summary.worker_online ? t('automation.status.online', 'Online') : t('automation.status.offline', 'Offline')}
        tone={summary.worker_online ? 'positive' : 'warning'}
      />
      <MetricPill
        icon={ScheduleIcon}
        label={t('automation.status.jobs', 'Jobs')}
        value={`${summary.enabled_jobs} / ${summary.total_jobs}`}
        tone="info"
      />
      <MetricPill
        icon={BoltIcon}
        label={t('automation.status.active', 'Active')}
        value={summary.active_jobs}
        tone={summary.active_jobs > 0 ? 'positive' : 'neutral'}
      />
      <MetricPill
        icon={QueueIcon}
        label={t('automation.status.queued', 'Queued')}
        value={summary.queued_jobs}
        tone={summary.queued_jobs > 0 ? 'warning' : 'neutral'}
      />
      <MetricPill
        icon={StorageIcon}
        label={t('automation.status.updated', 'Updated')}
        value={generatedAt}
        tone={runtimeBusy ? 'purple' : 'neutral'}
      />
    </Stack>
  );
}

AutomationStatusCards.propTypes = {
  summary: PropTypes.shape({
    worker_online: PropTypes.bool.isRequired,
    total_jobs: PropTypes.number.isRequired,
    enabled_jobs: PropTypes.number.isRequired,
    active_jobs: PropTypes.number.isRequired,
    queued_jobs: PropTypes.number.isRequired,
    schedule_source: PropTypes.string.isRequired,
    generated_at: PropTypes.string.isRequired,
  }).isRequired,
  t: PropTypes.func.isRequired,
};

export default AutomationStatusCards;